import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  FormFeedback,
  Alert,
  Button,
  Spinner,
} from "reactstrap";

interface CancelReasonModalProps {
  show: boolean;
  /** Modal title, e.g. "Cancel Purchase Order" */
  title?: string;
  /** Short description of what is being cancelled */
  description?: string;
  /** Called with the entered reason when the user confirms */
  onConfirm: (reason: string) => void;
  onClose: () => void;
  isSubmitting?: boolean;
  /** Server-side error to display inline (e.g. hierarchy/validation errors) */
  errorText?: string | null;
  /** Optional list of blocking records to display as bullets under the error */
  errorItems?: string[];
}

const CancelReasonModal: React.FC<CancelReasonModalProps> = ({
  show,
  title = "Cancel",
  description,
  onConfirm,
  onClose,
  isSubmitting = false,
  errorText = null,
  errorItems = [],
}) => {
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);

  // Reset local state whenever the modal is (re)opened
  useEffect(() => {
    if (show) {
      setReason("");
      setTouched(false);
    }
  }, [show]);

  const reasonInvalid = touched && !reason.trim();

  const handleConfirm = () => {
    setTouched(true);
    if (!reason.trim()) return;
    onConfirm(reason.trim());
  };

  return (
    <Modal isOpen={show} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>{title}</ModalHeader>
      <ModalBody>
        {description && <p className="text-muted">{description}</p>}

        {errorText && (
          <Alert color="danger" className="small mb-3">
            <div className="d-flex">
              <i className="ri-error-warning-line align-bottom me-1" />
              <span className="fw-semibold">{errorText}</span>
            </div>
            {errorItems && errorItems.length > 0 && (
              <ul className="mt-2 mb-0 ps-3">
                {errorItems.map((it, idx) => (
                  <li key={idx}>{it}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}

        <div className="mb-3">
          <Label for="cancel_reason" className="form-label">
            Reason for cancellation <span className="text-danger">*</span>
          </Label>
          <Input
            id="cancel_reason"
            type="textarea"
            rows={4}
            value={reason}
            placeholder="Enter the reason for cancelling..."
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            invalid={reasonInvalid}
          />
          <FormFeedback>A cancellation reason is required.</FormFeedback>
        </div>

        <div className="d-flex justify-content-end gap-2">
          <Button color="light" onClick={onClose} disabled={isSubmitting}>
            Close
          </Button>
          <Button color="danger" onClick={handleConfirm} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Spinner size="sm" className="me-1" /> Cancelling...
              </>
            ) : (
              "Confirm Cancellation"
            )}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default CancelReasonModal;
